import { map } from 'rxjs/operators';
import {
    faBed,
    faCheck,
    faCreditCard,
    faMoneyBill,
    faMoneyBillAlt,
    faMoneyBillWave,
    IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { HelperService } from 'libraries/hotel/services/helper.service';
import { QuotationService } from 'libraries/hotel/services/quotation/quotation.service';
import { Yii } from "libraries/interfaces/Yii";
import { IHotel } from 'libraries/hotel/models/hotel';
import { ICurrency } from 'libraries/service/resources/model/search/ICurrency';
import {
    SearchHotelFormService
} from "libraries/hotel/containers/search-hotel-form/services/search-hotel-form.service";
import { LabelService } from 'libraries/service/label/label.service';
import { IConfigSharedHotel } from 'libraries/hotel/models/config-shared-hotel';
import { IProperty } from "libraries/hotel/models/quote";
import { DateMomentPipe } from "libraries/date/dateMoment.pipe";
import { wenji } from "libraries/interfaces/IWenji";
import { ILabels } from 'libraries/interfaces/ILabels';
import { HotelQuoteStatus, ICurrencies } from 'libraries/service/resources/hotel/ISearchResponse';
import {EStayPermissions} from "../../../enums/e-stay-permissions";
import {PermissionResourceService} from "../../../service/resources/permission/permissionResource.service";
import {ITotal} from "../../../interfaces/ITotal";

interface IDated {
    begin: string;
    end: string;
    duration: {
        day: string;
        night: string;
    };
}

@Component({
    selector: 'hotel-resume',
    templateUrl: './hotel-resume.component.html',
    styleUrls: ['./hotel-resume.component.scss']
})
export class HotelResumeComponent implements OnInit {
    public readonly wording: ILabels = {
        webSite: Yii.t('wui-shared-js', 'Site internet'),
        request: Yii.t("wui-shared-js", "En demande"),
        roomWithFees: Yii.t('wui-shared-js', 'Chambre avec frais à payer sur place'),
        roomsWithFees: Yii.t('wui-shared-js', 'Chambres avec frais à payer sur place'),
        roomWithNoFee: Yii.t('wui-shared-js', 'Chambre sans frais à payer sur place :'),
        roomsWithNoFee: Yii.t('wui-shared-js', 'Chambres sans frais à payer sur place :'),
        costPrice: Yii.t('wui-shared-js', 'Prix d\'achat'),
        retailPrice: Yii.t('wui-shared-js', 'Prix de vente'),
    };

    @Output()
    public readonly onGetQuotes: EventEmitter<IHotel> = new EventEmitter();

    @Input()
    public hotel: IHotel;

    @Input()
    public oldHotel: IHotel;

    @Input()
    public currency: ICurrency;

    @Input()
    public currencies: ICurrencies;

    @Input()
    public configSharedHotel: IConfigSharedHotel;

    @Input()
    public hasRefreshError: boolean = false;

    @Input()
    public selectedQuoteProperty: IProperty;
    @Input()
    public isShowRooms: boolean;

    @Input()
    public directDisplay: {
        date?: boolean;
        duration?: boolean;
    } = {};

    @Input()
    public dates: IDated;

    @Input()
    public mealPlan: {
        code: string;
        name: string;
    };

    public iconBed: IconDefinition = faBed;

    public iconCheck: IconDefinition = faCheck;

    public maxNumberOfStars: Observable<number>;

    public HotelQuoteStatus: typeof HotelQuoteStatus = HotelQuoteStatus;

    public namesAndOccurencesList: string[];
    public namesWithFeesQuantity: number = 0;

    public moneyIcon: IconDefinition = faMoneyBillAlt;
    public roomIcon: IconDefinition = faBed;

    public permissions: typeof EStayPermissions = EStayPermissions;

    public showCostPricePermission: boolean = false;
    public showRetailPricePermission: boolean = false;

    private brand: string;

    constructor(
        private readonly helperService: HelperService,
        private readonly quotationService: QuotationService,
        private readonly searchHotelFormService: SearchHotelFormService,
        private readonly labelService: LabelService,
        private readonly dateMomentPipe: DateMomentPipe,
        private readonly permissionResource: PermissionResourceService,
    ) {
    }

    public ngOnInit(): void {
        this.permissionResource.getPermissionsStartingWith(['shared', 'stay', 'price']).subscribe((permissions: Set<string>) => {
            this.showCostPricePermission = permissions.has('shared.stay.price.cost.show');
            this.showRetailPricePermission = permissions.has('shared.stay.price.retail.show');
        });

        this.searchHotelFormService.getFormHotelData()
            .subscribe((response: { brand: string; }): void => {
                this.brand = response.brand;
            });

        this.maxNumberOfStars = this.searchHotelFormService.getAppDefault().pipe(
            map((config: any): number =>
                config
                && config.ratings
                && config.ratings.standardisationBody
                && config.ratings.standardisationBody.numberOfStars));

        if (this.hotel?.hostRoomNameArray?.length && this.isShowRooms) {
            this.namesWithFeesQuantity = this.hotel.hostRoomNameArray
                .filter((name: string): boolean => name.includes('- Fees (local) -'))
                .length;

            let nameList: string[] = [...new Set(this.hotel.hostRoomNameArray)];

            if (this.hotel.hostRoomNameArray.length !== nameList.length) {
                nameList = nameList
                    .map((name: string): string => {
                        const quantityFoundedInOrigin: number = this.hotel?.hostRoomNameArray
                            .filter((originName: string): boolean => originName === name)
                            .length;

                        return quantityFoundedInOrigin > 1 ? `${name} (x${quantityFoundedInOrigin})` : name;
                    });
            }

            this.namesAndOccurencesList = nameList;
        }
    }

    // Get the delta of the old and current price after refresh
    public getPriceDiff(oldPrice: { [key: string]: number }, currentPrice: { [key: string]: number }): number {
        return this.getPrice(oldPrice) - this.getPrice(currentPrice);
    }

    public getDateFormat(): string {
        return this.helperService.getDateFormat().momentShort;
    }

    public getQuotes(hotel: IHotel): void {
        this.onGetQuotes.emit(hotel);
    }

    public getAlternativeNames(alternative: string[]): string {
        return alternative.join(', ');
    }

    public getPrice(price: { [key: string]: number }): number {
        return this.quotationService.getPrice(price, this.currency);
    }

    public getBrandName(): string {
        return this.labelService.completeLabel(Yii.t('wui-shared-js', 'Hotel %placeholder% référencé'), this.brand);
    }

    public isBrandReferenced(): boolean {
        return this.hotel.rating && +this.hotel.rating.brand > 0;
    }

    /**
     * Get site url from hotel, or from selectedQuote
     */
    public hotelSite(): string {
        const quoteWebsite: string = this.selectedQuoteProperty
        && this.selectedQuoteProperty.sites
        && this.selectedQuoteProperty.sites.length ?
            this.selectedQuoteProperty.sites[0].url :
            null;
        const hotelWebsite: string = this.hotel
        && this.hotel.sites
        && this.hotel.sites.length ?
            this.hotel.sites[0].url :
            null;
        return hotelWebsite || quoteWebsite;
    }

    public dateInfos(): string {
        const begin: string = this.dateMomentPipe.transform(
            this.dates.begin,
            JSON.parse(wenji.ui.dateFormat).momentMedium);
        const end: string = this.dateMomentPipe.transform(
            this.dates.end,
            JSON.parse(wenji.ui.dateFormat).momentMedium);

        return this.labelService.completeLabel(
            Yii.t("wui-shared-js", "Du <strong>%begin%</strong> au <strong>%end%</strong>"),
            {begin, end}
        );
    }

    public durationInfos(): string {

        const dayString: string = Yii.t("wui-shared-js", "%countDays% jours");
        const nightString: string = Yii.t("wui-shared-js", "%countDays% nuits");

        const days: string = this.dates.duration && this.dates.duration.day ?
            this.labelService.completeLabel(
                dayString,
                {countDays: this.dates.duration.day}
            ) :
            null;
        const nights: string = this.dates.duration && this.dates.duration.night ?
            this.labelService.completeLabel(
                nightString,
                {countDays: this.dates.duration.night}
            ) :
            null;

        return days && nights ?
            `${days}, ${nights}` :
            days || nights;
    }

    public isString(value: unknown): boolean {
        return typeof value === 'string' || value instanceof String;
    }
}
